import { APIFieldType } from '@ardoq/api-types';
import {
  IntegrationReferenceDirection,
  ReferenceMapping,
  MissingComponentsStrategy,
  MissingParentComponentsStrategy,
  ParentMapping,
  ComponentPathMissingComponentsStrategy,
} from '@ardoq/api-types/integrations';
import { ColumnMapping } from 'integrations/common/streams/transferConfigs/types';
import { IconSize, IconName } from '@ardoq/icons';
import { defaultParentMapping } from 'integrations/common/streams/tabularMappings/defaultColumnMappings';
import { type I18nLocale, t } from '@ardoq/i18n';

const getColumnTypeNames = (
  i18nLocale: I18nLocale
): Record<ColumnMapping['columnType'], string> => ({
  'ardoq-oid': t(i18nLocale, 'integrations:columnType.ardoqOid', 'Ardoq OID'),
  component: t(
    i18nLocale,
    'integrations:columnType.component',
    'Component name'
  ),
  'component-key': t(
    i18nLocale,
    'integrations:columnType.componentKey',
    'Ardoq ID'
  ),
  'component-path': t(
    i18nLocale,
    'integrations:columnType.componentPath',
    'Component path'
  ),
  'component-type': t(
    i18nLocale,
    'integrations:columnType.componentType',
    'Type'
  ),
  'custom-id': t(i18nLocale, 'integrations:columnType.customId', 'Custom ID'),
  description: t(
    i18nLocale,
    'integrations:columnType.description',
    'Description'
  ),
  'display-text': t(
    i18nLocale,
    'integrations:columnType.displayText',
    'Display text'
  ),
  field: t(i18nLocale, 'integrations:columnType.field', 'Field'),
  parent: t(i18nLocale, 'integrations:columnType.parent', 'Parent component'),
  reference: t(i18nLocale, 'integrations:columnType.reference', 'Reference'),
  'reference-type': t(
    i18nLocale,
    'integrations:columnType.referenceType',
    'Type'
  ),
  'root-reference': t(
    i18nLocale,
    'integrations:columnType.rootReference',
    'Source'
  ),
  'target-reference': t(
    i18nLocale,
    'integrations:columnType.targetReference',
    'Target'
  ),
  tags: t(i18nLocale, 'integrations:columnType.tags', 'Tags'),
});

export const getColumnTypeName = (
  i18nLocale: I18nLocale,
  columnType: ColumnMapping['columnType']
): string => getColumnTypeNames(i18nLocale)[columnType];

export type IdentifierColumnType = 'custom-id' | 'ardoq-oid' | 'component-key';

// Single source of truth for the identifier explanations shown in both the
// component identifier column-type menu and the source/target reference format
// menu, so the two can't drift.
export const getIdentifierDescription = (
  i18nLocale: I18nLocale,
  identifier: IdentifierColumnType
): string =>
  ({
    'custom-id': t(
      i18nLocale,
      'integrations:columnTypeOption.customIdDescription',
      'User-defined identifier used for resources from other platforms (e.g. email, sys_id, etc.)'
    ),
    'ardoq-oid': t(
      i18nLocale,
      'integrations:columnTypeOption.ardoqOidDescription',
      'Unique internal identifier within Ardoq for components and references (e.g. 92a5dec3...)'
    ),
    'component-key': t(
      i18nLocale,
      'integrations:columnTypeOption.componentKeyDescription',
      'Human-readable identifier for components based on workspace name (e.g. APP-1)'
    ),
  })[identifier];

const getReferenceFormatNames = (
  i18nLocale: I18nLocale
): Record<ReferenceMapping['referenceFormat'], string> => ({
  path: t(i18nLocale, 'integrations:referenceFormat.path', 'Component path'),
  'ardoq-oid': t(
    i18nLocale,
    'integrations:referenceFormat.ardoqOid',
    'Ardoq OID'
  ),
  'custom-id': t(
    i18nLocale,
    'integrations:referenceFormat.customId',
    'Custom ID'
  ),
  'component-key': t(
    i18nLocale,
    'integrations:referenceFormat.componentKey',
    'Ardoq ID'
  ),
  name: t(i18nLocale, 'integrations:referenceFormat.name', 'Component name'),
});

export const getReferenceFormatName = (
  i18nLocale: I18nLocale,
  format: ReferenceMapping['referenceFormat']
): string => getReferenceFormatNames(i18nLocale)[format];

const getParentFormatNames = (
  i18nLocale: I18nLocale
): Record<ParentMapping['parentFormat'], string> => ({
  path: t(i18nLocale, 'integrations:parentFormat.path', 'Component path'),
  'ardoq-oid': t(i18nLocale, 'integrations:parentFormat.ardoqOid', 'Ardoq OID'),
  'custom-id': t(i18nLocale, 'integrations:parentFormat.customId', 'Custom ID'),
  'component-key': t(
    i18nLocale,
    'integrations:parentFormat.componentKey',
    'Ardoq ID'
  ),
  name: t(i18nLocale, 'integrations:parentFormat.name', 'Component name'),
});

export const getParentFormatName = (
  i18nLocale: I18nLocale,
  format: ParentMapping['parentFormat']
): string => getParentFormatNames(i18nLocale)[format];

export const isReferenceFormatDisabled = (
  format: ReferenceMapping['referenceFormat'],
  missingComponentsStrategy?: MissingComponentsStrategy
) =>
  missingComponentsStrategy === MissingComponentsStrategy.CREATE &&
  format !== 'path' &&
  format !== 'name';

export type ParentFormatValidation =
  | { isInvalid: true; popoverContent: string }
  | { isInvalid: false };

export const validateParentFormat = (
  i18nLocale: I18nLocale,
  format: ParentMapping['parentFormat'],
  strategy: MissingParentComponentsStrategy
): ParentFormatValidation => {
  if (strategy !== MissingParentComponentsStrategy.ERROR && format === 'path') {
    return {
      isInvalid: true,
      popoverContent: t(
        i18nLocale,
        'integrations:parentFormatValidation.notAvailable',
        { format: getParentFormatName(i18nLocale, format) },
        "This option is not available when mapping by '{{format}}'."
      ),
    };
  }

  if (
    strategy === MissingParentComponentsStrategy.EXCLUDE &&
    format !== 'custom-id' &&
    format !== 'component-key'
  ) {
    return {
      isInvalid: true,
      popoverContent: t(
        i18nLocale,
        'integrations:parentFormatValidation.useCustomIdOrComponentKey',
        {
          customIdFormat: getParentFormatName(i18nLocale, 'custom-id'),
          componentKeyFormat: getParentFormatName(i18nLocale, 'component-key'),
        },
        "To use this option, set the mapping format to '{{customIdFormat}}' or '{{componentKeyFormat}}' above."
      ),
    };
  }

  return { isInvalid: false };
};

export const getValidMissingComponentsStrategy = (
  i18nLocale: I18nLocale,
  newFormat: ParentMapping['parentFormat'],
  currentStrategy: MissingParentComponentsStrategy
): MissingParentComponentsStrategy => {
  const validation = validateParentFormat(
    i18nLocale,
    newFormat,
    currentStrategy
  );

  if (validation.isInvalid) {
    return defaultParentMapping().missingComponents!;
  }

  return currentStrategy;
};

const getReferenceDirectionNames = (
  i18nLocale: I18nLocale
): Record<IntegrationReferenceDirection, string> => ({
  outgoing: t(
    i18nLocale,
    'integrations:referenceDirection.outgoing',
    'Target component'
  ),
  incoming: t(
    i18nLocale,
    'integrations:referenceDirection.incoming',
    'Source component'
  ),
});

export const getReferenceDirectionName = (
  i18nLocale: I18nLocale,
  direction: IntegrationReferenceDirection
): string => getReferenceDirectionNames(i18nLocale)[direction];

const MAPPABLE_FIELD_TYPES = [
  APIFieldType.CHECKBOX,
  APIFieldType.DATE_ONLY,
  APIFieldType.DATE_TIME,
  APIFieldType.EMAIL,
  APIFieldType.FILE,
  APIFieldType.LIST,
  APIFieldType.NUMBER,
  APIFieldType.SELECT_MULTIPLE_LIST,
  APIFieldType.TEXT_AREA,
  APIFieldType.TEXT,
  APIFieldType.URL,
] as const;

type MappableFieldType = (typeof MAPPABLE_FIELD_TYPES)[number];

const FIELD_TYPE_ICONS: Record<MappableFieldType, IconName> = {
  [APIFieldType.CHECKBOX]: IconName.CHECKBOX_ITEM,
  [APIFieldType.DATE_ONLY]: IconName.DATE,
  [APIFieldType.DATE_TIME]: IconName.DATE_TIME,
  [APIFieldType.EMAIL]: IconName.AT,
  [APIFieldType.FILE]: IconName.FILE_UPLOADED,
  [APIFieldType.LIST]: IconName.LIST,
  [APIFieldType.NUMBER]: IconName.NUMBER,
  [APIFieldType.SELECT_MULTIPLE_LIST]: IconName.SELECT_LIST,
  [APIFieldType.TEXT_AREA]: IconName.TEXT_AREA,
  [APIFieldType.TEXT]: IconName.TEXT_FORMAT,
  [APIFieldType.URL]: IconName.REFERENCE,
};

const isMappableFieldType = (
  fieldType: string
): fieldType is MappableFieldType =>
  (MAPPABLE_FIELD_TYPES as readonly string[]).includes(fieldType);

const getMappableFieldTypeLabels = (
  i18nLocale: I18nLocale
): Record<MappableFieldType, string> => ({
  [APIFieldType.CHECKBOX]: t(
    i18nLocale,
    'integrations:fieldType.checkbox',
    'Checkbox'
  ),
  [APIFieldType.DATE_ONLY]: t(
    i18nLocale,
    'integrations:fieldType.dateOnly',
    'Date'
  ),
  [APIFieldType.DATE_TIME]: t(
    i18nLocale,
    'integrations:fieldType.dateTime',
    'Date time'
  ),
  [APIFieldType.EMAIL]: t(i18nLocale, 'integrations:fieldType.email', 'Email'),
  [APIFieldType.FILE]: t(i18nLocale, 'integrations:fieldType.file', 'File'),
  [APIFieldType.LIST]: t(i18nLocale, 'integrations:fieldType.list', 'List'),
  [APIFieldType.NUMBER]: t(
    i18nLocale,
    'integrations:fieldType.number',
    'Number'
  ),
  [APIFieldType.SELECT_MULTIPLE_LIST]: t(
    i18nLocale,
    'integrations:fieldType.selectMultipleList',
    'Select multiple list'
  ),
  [APIFieldType.TEXT_AREA]: t(
    i18nLocale,
    'integrations:fieldType.textArea',
    'Text paragraph'
  ),
  [APIFieldType.TEXT]: t(i18nLocale, 'integrations:fieldType.text', 'Text'),
  [APIFieldType.URL]: t(i18nLocale, 'integrations:fieldType.url', 'URL'),
});

export const getFieldTypeName = (
  i18nLocale: I18nLocale,
  fieldType: string
): string =>
  isMappableFieldType(fieldType)
    ? getMappableFieldTypeLabels(i18nLocale)[fieldType]
    : '';

export const getFieldTypeIcon = (fieldType: string) =>
  isMappableFieldType(fieldType)
    ? { size: IconSize.SMALL, name: FIELD_TYPE_ICONS[fieldType] }
    : undefined;

const getReferenceIfComponentIsMissingNames = (
  i18nLocale: I18nLocale
): Record<MissingComponentsStrategy, string> => {
  const exclude = t(
    i18nLocale,
    'integrations:referenceMissingComponents.exclude',
    'Exclude reference'
  );
  return {
    create: t(
      i18nLocale,
      'integrations:referenceMissingComponents.create',
      'Create new component'
    ),
    error: t(
      i18nLocale,
      'integrations:referenceMissingComponents.error',
      'Block the import'
    ),
    warn: exclude,
    'exclude-from-import': exclude,
  };
};

export const getReferenceIfComponentIsMissingName = (
  i18nLocale: I18nLocale,
  missingComponentsStrategy: MissingComponentsStrategy
): string =>
  getReferenceIfComponentIsMissingNames(i18nLocale)[missingComponentsStrategy];

const getParentIfComponentIsMissingNames = (
  i18nLocale: I18nLocale
): Record<MissingParentComponentsStrategy, string> => ({
  error: t(
    i18nLocale,
    'integrations:parentMissingComponents.error',
    'Block the import'
  ),
  'create-as-top-level': t(
    i18nLocale,
    'integrations:parentMissingComponents.createAsTopLevel',
    'Import as top-level components'
  ),
  'exclude-from-import': t(
    i18nLocale,
    'integrations:parentMissingComponents.exclude',
    'Exclude component from import'
  ),
});

export const getParentIfComponentIsMissingName = (
  i18nLocale: I18nLocale,
  missingComponentsStrategy: MissingParentComponentsStrategy
): string =>
  getParentIfComponentIsMissingNames(i18nLocale)[missingComponentsStrategy];

const getComponentPathIfComponentIsMissingNames = (
  i18nLocale: I18nLocale
): Record<ComponentPathMissingComponentsStrategy, string> => ({
  error: t(
    i18nLocale,
    'integrations:componentPathMissingComponents.error',
    'Block the import'
  ),
  'exclude-from-import': t(
    i18nLocale,
    'integrations:componentPathMissingComponents.exclude',
    'Exclude component from import'
  ),
});

export const getComponentPathIfComponentIsMissingName = (
  i18nLocale: I18nLocale,
  missingComponentsStrategy: ComponentPathMissingComponentsStrategy
): string =>
  getComponentPathIfComponentIsMissingNames(i18nLocale)[
    missingComponentsStrategy
  ];
